import { isPlatformServer } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { environment } from '@environment/environment';
import type {
	CreateUserDTO,
	UpdateOwnProfileDTO,
	UpdateUserDTO,
	UserFiltersDTO,
	UserListResponseDTO,
	UserResponseDTO,
} from '@libs/dtos/user/user.dto';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

/** User list page; the SSR/error fallback only carries data and total */
export type UserListPage = Pick<UserListResponseDTO, 'data' | 'total'> & Partial<UserListResponseDTO>;

@Injectable({
	providedIn: 'root',
})
export class UserService {
	private baseUrl = `${environment.pathBackend}/users`;
	private platformId = inject(PLATFORM_ID);

	constructor(private http: HttpClient) {}

	/**
	 * Checks if we're running on server side
	 */
	private isServer(): boolean {
		return isPlatformServer(this.platformId);
	}

	/**
	 * Get all users with pagination and filters
	 */
	getAllUsers(params: Partial<UserFiltersDTO>): Observable<UserListPage> {
		if (this.isServer()) {
			return of({ data: [], total: 0 });
		}

		let httpParams = new HttpParams();
		for (const [key, value] of Object.entries(params)) {
			if (value !== undefined) {
				httpParams = httpParams.set(key, value);
			}
		}

		// No catchError: callers show the load error (a failed request is not an empty list)
		return this.http.get<UserListResponseDTO>(this.baseUrl, { params: httpParams });
	}

	/**
	 * Retrieves a paginated list of users from the backend with optional filters.
	 * @returns An Observable containing the filtered and paginated user data.
	 */
	getUsers(
		page: number,
		limit: number,
		sortBy?: string,
		sortOrder?: string,
		role?: string,
	): Observable<UserListResponseDTO> {
		let params = new HttpParams().set('page', page.toString()).set('limit', limit.toString());

		if (sortBy) params = params.set('sort_by', sortBy);
		if (sortOrder) params = params.set('sort_order', sortOrder);
		if (role) params = params.set('role', role);

		return this.http.get<UserListResponseDTO>(this.baseUrl, { params }).pipe(
			catchError(() => {
				return throwError(() => new Error('Failed to fetch users'));
			}),
		);
	}

	/**
	 * Retrieves a user by its ID.
	 */
	getUserById(id: string): Observable<UserResponseDTO> {
		return this.http.get<UserResponseDTO>(`${this.baseUrl}/${id}`).pipe(
			catchError(() => {
				return throwError(() => new Error('Failed to fetch user'));
			}),
		);
	}

	/**
	 * Creates a new user.
	 */
	createUser(userData: CreateUserDTO): Observable<UserResponseDTO> {
		// No catchError: the caller shows the backend message (err.error.message)
		return this.http.post<UserResponseDTO>(this.baseUrl, userData);
	}

	/**
	 * Updates an existing user (requires users:update permission).
	 */
	updateUser(id: string, userData: UpdateUserDTO): Observable<UserResponseDTO> {
		// No catchError: the caller shows the backend message (err.error.message)
		return this.http.put<UserResponseDTO>(`${this.baseUrl}/${id}`, userData);
	}

	/**
	 * Gets the authenticated user's own profile (no permission required).
	 */
	getMyProfile(): Observable<UserResponseDTO> {
		return this.http.get<UserResponseDTO>(`${this.baseUrl}/me`).pipe(
			catchError(() => {
				return throwError(() => new Error('Failed to fetch profile'));
			}),
		);
	}

	/**
	 * Gets the authenticated user's own profile photo URL (no permission required).
	 */
	getMyProfilePhoto(): Observable<{ url: string | null }> {
		return this.http.get<{ url: string | null }>(`${this.baseUrl}/me/profile-photo`).pipe(
			catchError(() => {
				return throwError(() => new Error('Failed to fetch profile photo'));
			}),
		);
	}

	/**
	 * Uploads a profile photo for the authenticated user (no permission required).
	 */
	uploadMyProfilePhoto(file: File): Observable<{ url: string; message: string }> {
		const formData = new FormData();
		formData.append('file', file);
		return this.http
			.post<{ url: string; message: string }>(`${this.baseUrl}/me/profile-photo/upload`, formData)
			.pipe(
				catchError(() => {
					return throwError(() => new Error('Failed to upload profile photo'));
				}),
			);
	}

	/**
	 * Updates the authenticated user's own profile (no permission required).
	 */
	/**
	 * Own profile. Changing the email needs `currentPassword` (400 CURRENT_PASSWORD_REQUIRED /
	 * 401 INVALID_CURRENT_PASSWORD). No catchError: the caller shows the backend's (translated) message.
	 */
	updateOwnProfile(userData: UpdateOwnProfileDTO & { currentPassword?: string }): Observable<UserResponseDTO> {
		return this.http.put<UserResponseDTO>(`${this.baseUrl}/me`, userData);
	}

	/**
	 * Delete user
	 */
	deleteUser(id: number): Observable<{ success: boolean; message: string } | null> {
		if (this.isServer()) {
			return of(null);
		}

		return this.http.delete<{ success: boolean; message: string }>(`${this.baseUrl}/${id}`).pipe(
			catchError((error) => {
				throw error;
			}),
		);
	}

	/**
	 * Deletes one or multiple users.
	 */
	deleteUsers(ids: string[]): Observable<{ success: boolean }> {
		const payload = { ids };

		return this.http
			.request<{ success: boolean }>('DELETE', this.baseUrl, {
				body: payload,
				headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
			})
			.pipe(
				catchError(() => {
					return throwError(() => new Error('Failed to delete users'));
				}),
			);
	}

	/** The logged-in user changes their own password (current password required) */
	updateOwnPassword(passwordData: { currentPassword: string; password: string }): Observable<void> {
		return this.http.put<void>(`${this.baseUrl}/me/password`, passwordData);
	}

	/**
	 * Ban a user
	 * @param userId User's numeric ID
	 * @returns Observable with success message
	 */
	banUser(userId: number): Observable<{ success: boolean; message: string }> {
		if (this.isServer()) {
			return of({ success: false, message: 'Server side rendering' });
		}

		return this.http.post<{ success: boolean; message: string }>(`${this.baseUrl}/${userId}/ban`, {});
	}

	/**
	 * Unban a user
	 * @param userId User's numeric ID
	 * @returns Observable with success message
	 */
	unbanUser(userId: number): Observable<{ success: boolean; message: string }> {
		if (this.isServer()) {
			return of({ success: false, message: 'Server side rendering' });
		}

		return this.http.post<{ success: boolean; message: string }>(`${this.baseUrl}/${userId}/unban`, {});
	}
}
